import { ExternalLinkIcon } from '@components/Icons/ExternalLinkIcon';
import Loader from '@components/Loader';
import NapsterSpinner from '@components/NapsterSpinner';
import {
  Node as FlowNode,
  Position as FlowPosition,
  Handle,
  NodeProps,
} from '@xyflow/react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LyricsArtifact } from '../LyricsArtifact';
import { ProduceButton } from '../ProduceButton';
import { clamp } from './constants';
import type { NodeSize, WorkflowCardData, WorkflowMode } from './types';

const handleStyle: React.CSSProperties = {
  opacity: 0,
  width: 1,
  height: 1,
};

export type AvatarNodeData = {
  layoutMode: 'desktop';
  diameter: number;
  avatarReady: boolean;
};

type AvatarNode = FlowNode<AvatarNodeData>;

export const AvatarNodeComponent = ({ data }: NodeProps<AvatarNode>) => (
  <div
    className="pointer-events-none flex justify-center"
    style={{
      width: data.diameter,
      height: data.diameter,
    }}>
    <div
      id="workflow-avatar-proxy"
      className="pointer-events-auto"
      style={{
        width: data.diameter,
        height: data.diameter,
      }}
    />
    <Handle
      type="source"
      id="right"
      position={FlowPosition.Right}
      style={handleStyle}
    />
    <Handle
      type="source"
      id="bottom"
      position={FlowPosition.Bottom}
      style={handleStyle}
    />
  </div>
);

export type LyricsNodeData = {
  layoutMode: 'desktop';
  lyrics: string | null;
  title?: string;
  isLoading: boolean;
  onLyricsChange?: (editedLyrics: string) => void;
  onTitleChange?: (editedTitle: string) => void;
  size?: NodeSize;
};

type LyricsNode = FlowNode<LyricsNodeData>;

export const LyricsNodeComponent = ({ data }: NodeProps<LyricsNode>) => (
  <div
    className="pointer-events-auto"
    style={
      data.size
        ? {
            width: data.size.width,
            height: data.size.height,
          }
        : undefined
    }>
    <Handle
      type="target"
      id="left"
      position={FlowPosition.Left}
      style={handleStyle}
    />
    <Handle
      type="target"
      id="top"
      position={FlowPosition.Top}
      style={handleStyle}
    />
    <Handle
      type="source"
      id="right"
      position={FlowPosition.Right}
      style={handleStyle}
    />
    <Handle
      type="source"
      id="bottom"
      position={FlowPosition.Bottom}
      style={handleStyle}
    />
    <LyricsArtifact
      lyrics={data.lyrics}
      title={data.title}
      isLoading={data.isLoading}
      onLyricsChange={data.onLyricsChange}
      onTitleChange={data.onTitleChange}
      className="transform transition-all duration-500 h-full"
      style={
        data.size
          ? {
              width: data.size.width,
              height: data.size.height,
            }
          : undefined
      }
    />
  </div>
);

export type ProduceNodeData = {
  layoutMode: 'desktop';
  onProduce: () => void;
  isProducing: boolean;
  disabled: boolean;
  onSizeChange?: (size: NodeSize) => void;
  size?: NodeSize;
  stackedView?: boolean;
};

type ProduceNode = FlowNode<ProduceNodeData>;

export const ProduceNodeComponent = ({ data }: NodeProps<ProduceNode>) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!containerRef.current || !data.onSizeChange) {
      return;
    }

    const element = containerRef.current;

    const emitSize = () => {
      const rect = element.getBoundingClientRect();
      data.onSizeChange?.({ width: rect.width, height: rect.height });
    };

    emitSize();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      if (!entries.length) {
        return;
      }
      const { width, height } = entries[0].contentRect;
      data.onSizeChange?.({ width, height });
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [data.onSizeChange, data.isProducing, data.disabled]);

  return (
    <div
      ref={containerRef}
      className="pointer-events-auto inline-flex items-center justify-center"
      style={
        data.stackedView && data.size?.width
          ? {
              width: data.size.width,
            }
          : undefined
      }>
      <Handle
        type="target"
        id="left"
        position={FlowPosition.Left}
        style={handleStyle}
      />
      <Handle
        type="target"
        id="top"
        position={FlowPosition.Top}
        style={handleStyle}
      />
      <Handle
        type="source"
        id="right"
        position={FlowPosition.Right}
        style={handleStyle}
      />
      <Handle
        type="source"
        id="bottom"
        position={FlowPosition.Bottom}
        style={handleStyle}
      />
      <ProduceButton
        onClick={data.onProduce}
        isProducing={data.isProducing}
        disabled={data.disabled}
        className="transform transition-all duration-500"
      />
    </div>
  );
};

export type CountdownNodeData = {
  layoutMode: 'desktop';
  countdownSeconds: number | null;
  showExtendedMessage: boolean;
  size?: NodeSize;
};

type CountdownNode = FlowNode<CountdownNodeData>;

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const CountdownNodeComponent = ({ data }: NodeProps<CountdownNode>) => {
  const { t } = useTranslation();
  if (!data.countdownSeconds && !data.showExtendedMessage) {
    return null;
  }

  const displayText = data.showExtendedMessage
    ? t('generation.fewMoreSeconds')
    : data.countdownSeconds !== null && data.countdownSeconds > 0
    ? formatTime(data.countdownSeconds)
    : null;

  if (!displayText) {
    return null;
  }

  return (
    <div className="pointer-events-none flex items-center justify-center w-full">
      <Handle
        type="target"
        id="top"
        position={FlowPosition.Top}
        style={handleStyle}
      />
      <div className="text-white/80 text-sm font-medium text-center px-3 py-1.5 rounded-lg bg-black/40 backdrop-blur-sm whitespace-nowrap">
        {displayText}
      </div>
    </div>
  );
};

export type SongCardNodeData = {
  layoutMode: 'desktop';
  card: WorkflowCardData;
  size?: NodeSize;
  mode: WorkflowMode;
};

type SongCardNode = FlowNode<SongCardNodeData>;

export const SongCardNodeComponent = ({ data }: NodeProps<SongCardNode>) => {
  const { t } = useTranslation();
  const { card, mode } = data;
  const width = data.size?.width ?? 250;
  const height = data.size?.height ?? 320;
  const isMusic = mode === 'music';

  const footerHeight = clamp(height * 0.22, 72, 110);
  const targetMediaHeight = isMusic ? width : width / (16 / 9);
  const mediaHeight = Math.min(
    Math.max(140, height - footerHeight),
    targetMediaHeight,
  );
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [promptValue, setPromptValue] = useState('');
  const containerClassName = `relative pointer-events-auto generating-card animating flex flex-col rounded-2xl ${
    card.isSelected
      ? 'ring-2 ring-white ring-offset-2 ring-offset-black/40'
      : ''
  } ${isPromptOpen ? 'nodrag nowheel' : ''}`;
  const containerStyle: React.CSSProperties = {
    width,
    height,
    cursor: isPromptOpen ? 'default' : 'pointer',
  };

  const flipInnerStyle: React.CSSProperties = {
    transform: isPromptOpen ? 'rotateY(180deg)' : 'rotateY(0deg)',
    transformStyle: 'preserve-3d',
    transition: 'transform 500ms',
    width,
    height,
    position: 'relative',
  };

  const faceStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
    borderRadius: '16px',
    overflow: 'hidden',
    zIndex: 99,
  };

  const backFaceStyle: React.CSSProperties = {
    ...faceStyle,
    transform: 'rotateY(180deg)',
  };

  useEffect(() => {
    if (card.isRegenerating) {
      setIsPromptOpen(false);
    }
  }, [card.isRegenerating]);

  const renderHandles = () => (
    <>
      <Handle
        type="target"
        id="left"
        position={FlowPosition.Left}
        style={handleStyle}
      />
      <Handle
        type="target"
        id="top"
        position={FlowPosition.Top}
        style={handleStyle}
      />
      <Handle
        type="source"
        id="right"
        position={FlowPosition.Right}
        style={handleStyle}
      />
      <Handle
        type="source"
        id="bottom"
        position={FlowPosition.Bottom}
        style={handleStyle}
      />
    </>
  );

  if (!card.visible) {
    return (
      <div className="pointer-events-none" style={{ width, height }}>
        {renderHandles()}
        <div className="w-full h-full rounded-2xl border border-white/15 bg-white/5 backdrop-blur-sm flex items-center justify-center text-xs md:text-sm text-white/50">
          {t('generation.waitingForGeneration')}
        </div>
      </div>
    );
  }

  const handleCardClick = () => {
    if (card.isDesignerMode && card.imageUrl) {
      card.onClick();
    } else if (!card.isDesignerMode && card.imageUrl) {
      card.onClick();
    }
  };

  const handlePromptToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsPromptOpen((prev) => !prev);
  };

  const handlePromptChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setPromptValue(event.target.value);
  };

  const handlePromptCancel = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsPromptOpen(false);
    setPromptValue('');
  };

  const handlePromptSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const trimmed = promptValue.trim();
    if (!trimmed || card.isRegenerating) {
      return;
    }
    card.onRegenerate(trimmed);
    setPromptValue('');
    setIsPromptOpen(false);
  };

  return (
    <div
      className={containerClassName}
      style={containerStyle}
      onClick={handleCardClick}>
      {card.isSelected && !isPromptOpen && (
        <div className="pointer-events-none absolute inset-[-2px] rounded-2xl border-2 border-white z-30" />
      )}
      {renderHandles()}
      {card.imageUrl && !isPromptOpen && (
        <button
          type="button"
          onClick={handlePromptToggle}
          disabled={card.isRegenerating}
          className="absolute left-3 top-3 z-40 rounded-full bg-black/70 px-2 py-1 text-xs font-medium text-white transition hover:bg-black/80 disabled:opacity-60 disabled:hover:bg-black/70">
          {t('generation.prompt')}
        </button>
      )}

      <div style={flipInnerStyle}>
        {(card.showGradient || card.isRegenerating) && (
          <div
            className="gradient-border"
            style={
              {
                position: 'absolute',
                top: card.isSelected ? '-4px' : '-2px',
                left: card.isSelected ? '-4px' : '-2px',
                right: card.isSelected ? '-4px' : '-2px',
                bottom: card.isSelected ? '-4px' : '-2px',
                borderRadius: card.isSelected ? '16px' : '14px',
                pointerEvents: 'none',
                '--rotation': '0deg',
                background:
                  'conic-gradient(from calc(0deg + var(--rotation)), var(--Tertiary, #F6911D) 0deg, var(--Secondary, #FF04FF) 120deg, var(--Primary, #0FF) 240deg, var(--Tertiary, #F6911D) 360deg)',
                zIndex: 1,
              } as React.CSSProperties & { '--rotation': string }
            }
          />
        )}
        <div style={faceStyle} className="group">
          <div
            className={`overflow-hidden shadow-2xl bg-black/80 ${
              card.isDesignerMode || card.isReady
                ? 'rounded-t-xl'
                : 'rounded-xl'
            } relative flex items-center justify-center z-10`}
            style={{
              width: '100%',
              height: mediaHeight,
            }}>
            {card.imageUrl ? (
              <img
                src={card.imageUrl}
                alt={t('generation.generatedCover')}
                className="w-full h-full object-cover pointer-events-none"
              />
            ) : (
              <Loader size="small" />
            )}

            <div
              className="absolute bottom-3 right-3 opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity duration-200 cursor-pointer"
              onClick={(event) => {
                event.stopPropagation();
                card.onActionClick();
              }}>
              <div className="w-12 h-12 bg-black/80 rounded-full flex items-center justify-center shadow-lg">
                {card.actionType === 'external' ? (
                  card.actionReady ? (
                    <ExternalLinkIcon
                      width={24}
                      height={24}
                      className="text-white"
                    />
                  ) : (
                    <NapsterSpinner size="small" />
                  )
                ) : card.actionReady ? (
                  card.isPlaying ? (
                    <svg
                      width={24}
                      height={24}
                      viewBox="0 0 24 24"
                      fill="none"
                      className="text-white">
                      <rect
                        x={6}
                        y={6}
                        width={4}
                        height={12}
                        rx={1}
                        fill="currentColor"
                      />
                      <rect
                        x={14}
                        y={6}
                        width={4}
                        height={12}
                        rx={1}
                        fill="currentColor"
                      />
                    </svg>
                  ) : (
                    <div className="w-0 h-0 border-l-[10px] border-l-white border-y-[7px] border-y-transparent ml-1" />
                  )
                ) : (
                  <NapsterSpinner size="small" />
                )}
              </div>
            </div>
          </div>

          <div
            className="rounded-b-xl bg-black/30 backdrop-blur-xs px-3 py-2 relative z-10"
            style={{ height: footerHeight }}>
            <p className="text-white text-sm font-medium truncate">
              {card.title}
            </p>
            <p className="text-white/80 text-xs truncate">{card.subtitle}</p>
          </div>
        </div>

        <div style={backFaceStyle}>
          <div
            className="absolute inset-0 flex h-full w-full flex-col rounded-2xl bg-black/90 p-4 backdrop-blur-sm"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}>
            <textarea
              value={promptValue}
              onChange={handlePromptChange}
              placeholder={t('generation.regeneratePlaceholder')}
              className="flex-1 resize-none rounded-lg border border-white/20 bg-black/40 p-3 text-sm text-white outline-none focus:border-white/60 nodrag nopan"
              onMouseDown={(event) => event.stopPropagation()}
              onTouchStart={(event) => event.stopPropagation()}
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={handlePromptCancel}
                className="rounded-lg border border-white/20 px-3 py-1.5 text-xs font-medium text-white transition hover:border-white/40 hover:bg-white/10">
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handlePromptSubmit}
                disabled={!promptValue.trim() || card.isRegenerating}
                className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-black transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-60">
                {t('generation.regenerate')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const nodeTypes = {
  avatar: AvatarNodeComponent,
  lyrics: LyricsNodeComponent,
  produce: ProduceNodeComponent,
  songCard: SongCardNodeComponent,
  countdown: CountdownNodeComponent,
};
